import { Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/lib/auth-context";
import { useAdminDeal } from "./use-admin-deal";
import { DealDrawerContent } from "./deal-drawer-content";

// SPA-side wrapper for the deal drawer. The drawer mounts only when a
// `hubspotDealId` is passed in (the parent page derives it from the
// `?deal=` URL param) and unmounts on close so the next open re-runs the
// query for a fresh payload.
//
// Loading + error states render inside an open Sheet so the user gets
// visual confirmation that the click registered.

export function DealDrawer({
  hubspotDealId,
  onClose,
}: {
  hubspotDealId: string | null;
  onClose: () => void;
}) {
  const session = useAuth().session;
  const canManage = session?.role === "internal";
  const query = useAdminDeal(hubspotDealId);

  if (!hubspotDealId) return null;

  if (query.isLoading) {
    return (
      <Sheet
        open
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
      >
        <SheetContent
          side="right"
          className="w-full overflow-y-auto p-0 sm:max-w-[640px]"
        >
          <SheetHeader className="border-b px-6 py-4">
            <SheetTitle className="font-display tracking-tight">
              Loading…
            </SheetTitle>
            <SheetDescription className="sr-only">
              Loading deal details
            </SheetDescription>
          </SheetHeader>
          <div className="flex items-center justify-center px-6 py-12 text-xs text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading deal…
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Sheet
        open
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
      >
        <SheetContent
          side="right"
          className="w-full overflow-y-auto p-0 sm:max-w-[640px]"
        >
          <SheetHeader className="border-b px-6 py-4">
            <SheetTitle className="font-display tracking-tight">
              Couldn&apos;t load deal
            </SheetTitle>
            <SheetDescription className="text-xs">
              {query.error?.message ??
                "The deal couldn't be loaded. It may have been removed."}
            </SheetDescription>
          </SheetHeader>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <DealDrawerContent
      payload={query.data}
      canManage={canManage}
      open
      onClose={onClose}
    />
  );
}
